import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form, Card, Badge, Row, Col, InputGroup } from 'react-bootstrap';

// Şirket veri tipi tanımı
interface Company {
  id: number;
  name: string;
  industry: string;
  size: string;
  location: string;
  website: string;
  revenue: string;
  employees: number;
  founded: number;
  description: string;
  competitors: string[];
  marketShare: number;
  growth: number;
}

// Pazar analizi veri tipi tanımı
interface MarketMetric {
  title: string;
  value: string | number | string[];
  trend: number;
  color: string;
  icon: string;
}

// Örnek şirket verileri
const dummyCompanies: Company[] = [
  {
    id: 1,
    name: 'Tech Solutions Inc.',
    industry: 'Technology',
    size: '500-1000',
    location: 'San Francisco, CA',
    website: 'www.techsolutions.com',
    revenue: '$50M - $100M',
    employees: 750,
    founded: 2010,
    description: 'Leading provider of enterprise software solutions.',
    competitors: ['Innovation Labs', 'Global Tech', 'Future Systems'],
    marketShare: 15,
    growth: 25,
  },
  {
    id: 2,
    name: 'Global Industries',
    industry: 'Manufacturing',
    size: '1000-5000',
    location: 'New York, NY',
    website: 'www.globalind.com',
    revenue: '$100M - $500M',
    employees: 2500,
    founded: 1995,
    description: 'Global manufacturing and distribution company.',
    competitors: ['Industrial Corp', 'Manufacturing Pro', 'Global Tech'],
    marketShare: 22,
    growth: 18,
  },
];

// Pazar analizi verileri
const marketAnalysis: MarketMetric[] = [
  {
    title: 'Total Companies',
    value: 150,
    trend: 12.5,
    color: 'primary',
    icon: 'bi-building',
  },
  {
    title: 'Average Revenue',
    value: '$75M',
    trend: 12.5,
    color: 'success',
    icon: 'bi-currency-dollar',
  },
  {
    title: 'Market Growth',
    value: '12.5%',
    trend: 12.5,
    color: 'warning',
    icon: 'bi-graph-up',
  },
  {
    title: 'Top Industries',
    value: ['Technology', 'Manufacturing', 'Healthcare', 'Retail'],
    trend: 12.5,
    color: 'info',
    icon: 'bi-bar-chart',
  },
];

// Sektör listesi
const industries = [
  'Technology',
  'Manufacturing',
  'Healthcare',
  'Retail',
  'Finance',
  'Education',
  'Energy',
  'Transportation',
];

const CompanyResearchPage: React.FC = () => {
  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Şirketleri filtreleme
  const filteredCompanies = dummyCompanies.filter(
    company =>
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.industry.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Modal işleyicileri
  const handleCloseModal = () => setShowNewCompanyModal(false);
  const handleShowModal = () => setShowNewCompanyModal(true);

  return (
    <div className="container-fluid py-4">
      {/* Başlık Bölümü */}
      <div className="mb-4">
        <h2 className="fw-bold mb-2">Company Research</h2>
        <p className="text-muted">
          Analyze and track company profiles, market trends, and competitor insights
        </p>
      </div>

      {/* Arama ve Filtre Bölümü */}
      <Card className="mb-4 shadow-sm border-0 rounded-3">
        <Card.Body>
          <Row className="align-items-center">
            <Col xs={12} md={6}>
              <InputGroup>
                <InputGroup.Text className="bg-transparent border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={6} className="text-md-end mt-3 mt-md-0">
              <Button
                variant="primary"
                className="rounded-3"
                onClick={handleShowModal}
                style={{
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  border: 'none',
                }}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Add Company
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Pazar Genel Bakış Kartları */}
      <Row className="mb-4 g-3">
        {marketAnalysis.map((metric, index) => (
          <Col xs={12} sm={6} md={3} key={index}>
            <Card className="h-100 shadow-sm border-0 rounded-3">
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div 
                    className={`d-flex align-items-center justify-content-center rounded-circle bg-${metric.color} bg-opacity-10 me-3`}
                    style={{ width: '48px', height: '48px' }}
                  >
                    <i className={`${metric.icon} fs-4 text-${metric.color}`}></i>
                  </div>
                  <h6 className="fw-bold mb-0">{metric.title}</h6>
                </div>
                
                <h3 className="fw-bold mb-2">
                  {Array.isArray(metric.value) 
                    ? metric.value.slice(0, 2).join(', ') + (metric.value.length > 2 ? '...' : '')
                    : metric.value}
                </h3>
                
                <div className={`d-flex align-items-center text-${metric.trend > 0 ? 'success' : 'danger'}`}>
                  <i className={`bi ${metric.trend > 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow'} me-1`}></i>
                  <small>{Math.abs(metric.trend)}% from last month</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Şirket Listesi */}
      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body>
          <ul className="list-group list-group-flush">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company, index) => (
                <li key={company.id} className="list-group-item px-0 py-3 border-bottom">
                  <div className="d-flex flex-column flex-md-row">
                    <div className="d-flex align-items-center mb-3 mb-md-0">
                      <div 
                        className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 me-3"
                        style={{ width: '56px', height: '56px' }}
                      >
                        <i className="bi bi-building fs-3 text-primary"></i>
                      </div>
                    </div>
                    
                    <div className="flex-grow-1">
                      <h5 className="fw-bold mb-2">{company.name}</h5>
                      <p className="text-muted mb-2">{company.description}</p>
                      <div className="d-flex flex-wrap gap-2">
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                          <i className="bi bi-building me-1"></i> {company.industry}
                        </Badge>
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                          <i className="bi bi-people me-1"></i> {company.employees} employees
                        </Badge>
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                          <i className="bi bi-geo-alt me-1"></i> {company.location}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="d-flex mt-3 mt-md-0 align-items-start">
                      <Button 
                        variant="primary" 
                        size="sm" 
                        className="me-2 rounded-3"
                        style={{ width: '36px', height: '36px', padding: '0' }}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        className="rounded-3"
                        style={{ width: '36px', height: '36px', padding: '0' }}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                </li>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">No companies found matching your search criteria.</p>
              </div>
            )}
          </ul>
        </Card.Body>
      </Card>

      {/* Yeni Şirket Ekleme Modal */}
      <Modal show={showNewCompanyModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Form.Control type="text" placeholder="Enter company name" />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Industry</Form.Label>
              <Form.Select>
                <option value="">Select an industry</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Enter company description" />
            </Form.Group>
            
            <Row>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control type="text" placeholder="City, State" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Employees</Form.Label>
                  <Form.Control type="number" placeholder="Number of employees" />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCloseModal}
            style={{
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              border: 'none',
            }}
          >
            Add Company
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanyResearchPage;
